import React, { useEffect, useState } from "react";
import Highcharts, { Options } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Boost from "highcharts/modules/boost";

Boost(Highcharts); // Enable boost module

const chartTypes = [
  "Line Chart",
  "Bar Chart",
  "Column Chart",
  "Area Chart",
  "Spline Chart",
  "Pie Chart",
  "Table",
] as const;

type ChartType = typeof chartTypes[number];
type HighchartType = "line" | "bar" | "column" | "area" | "spline" | "pie";

const chartTypeMap: Record<Exclude<ChartType, "Table">, HighchartType> = {
  "Line Chart": "line",
  "Bar Chart": "bar",
  "Column Chart": "column",
  "Area Chart": "area",
  "Spline Chart": "spline",
  "Pie Chart": "pie",
};

interface SeriesData {
  name: string;
  data: number[];
  type?: HighchartType;
}

interface ChartData {
  name: string;
  categories: string[];
  series: SeriesData[];
}

interface ParsedChart {
  chart_data: {
    name: string;
    xAxis: string[];
    series: SeriesData[];
  };
  summary?: string;
}

interface HighchartFromTextProps {
  chartText: string;
  source?: string;
}

const HighchartFromText: React.FC<HighchartFromTextProps> = ({ chartText, source }) => {
  const [originalData, setOriginalData] = useState<ChartData | null>(null);
  const [options, setOptions] = useState<Options | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<ChartType>("Line Chart");

  useEffect(() => {
    try {
      if (chartText) {
        const parsed: ParsedChart = JSON.parse(chartText);
        if (parsed?.chart_data) {
          setSummary(parsed.summary || null);
          setOriginalData({
            name: parsed.chart_data.name,
            categories: parsed.chart_data.xAxis,
            series: parsed.chart_data.series,
          });
          setSelectedType("Table");
        }
      }
    } catch (err) {
      console.error("Error parsing chartText JSON:", err);
    }
  }, [chartText]);

  useEffect(() => {
    if (!originalData) return;

    if (selectedType === "Table") {
      setOptions(null);
    } else {
      const newOptions: Options = {
        title: { text: originalData.name },
        xAxis: {
          categories: originalData.categories,
        },
        yAxis: {
          title: { text: "Value" },
        },
        tooltip: {
          shared: true,
        },
        series: originalData.series.map((s) => ({
          ...s,
          type: chartTypeMap[selectedType as Exclude<ChartType, "Table">],
        })),
      };
      setOptions(newOptions);
    }
  }, [selectedType, originalData]);

  const renderTable = () => {
    if (!originalData) return <p>No data available.</p>;

    const { categories, series } = originalData;
    const rowCount = 5;
    const startIndex = Math.max(0, categories.length - rowCount);
    const visibleCategories = categories.slice(startIndex);

    return (
      <table className="table table-bordered table-striped">
        <thead>
          <tr>
            <th>Timestamp</th>
            {series.map((s, idx) => (
              <th key={idx}>{s.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleCategories.map((cat, idx) => (
            <tr key={idx}>
              <td>{cat}</td>
              {series.map((s, sIdx) => (
                <td key={sIdx}>{s.data[startIndex + idx]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div>
      {selectedType === "Table" ? (
        <>
          <div className="d-flex justify-content-end mb-2 align-items-center">
            <select
              value={selectedType}
              className="border-0 rounded-2 p-2"
              onChange={(e) => setSelectedType(e.target.value as ChartType)}
            >
              {chartTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          {renderTable()}
        </>
      ) : options ? (
        <>
          <div className="d-flex justify-content-end mb-2 align-items-center">
            <select
              value={selectedType}
              className="border-0 rounded-2 p-2"
              onChange={(e) => setSelectedType(e.target.value as ChartType)}
            >
              {chartTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <HighchartsReact highcharts={Highcharts} options={options} />
        </>
      ) : (
        <>
          <p
            className="text-center"
            style={{
              fontWeight: "bold",
              color: "#000",
              animation: "blink 1s infinite",
            }}
          >
            ... Loading Data ...
          </p>
          <style>
            {`
              @keyframes blink {
                0%, 100% { opacity: 1; }
                50% { opacity: 0; }
              }
            `}
          </style>
        </>
      )}
      {summary && (
        <p>
          <b>{summary}</b>
        </p>
      )}
    </div>
  );
};

export default HighchartFromText;
